import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, CheckCircle2, ArrowRight, Coins, DollarSign, Plus, Minus, Target } from 'lucide-react';
import CryptoAffiliateBlock from '../../CryptoAffiliateBlock';

const CryptoGainCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [buyFee, setBuyFee] = useState('');
  const [sellFee, setSellFee] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, quantity, buyFee, sellFee]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const qty = parseFloat(quantity);
    const bFee = parseFloat(buyFee) || 0;
    const sFee = parseFloat(sellFee) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(qty) || buy <= 0 || qty <= 0) {
      setResult(null);
      return;
    }

    // Cost basis
    const purchaseCost = buy * qty;
    const totalBuyFee = purchaseCost * (bFee / 100);
    const totalCostBasis = purchaseCost + totalBuyFee;

    // Sale proceeds
    const saleProceeds = sell * qty;
    const totalSellFee = saleProceeds * (sFee / 100);
    const netSaleProceeds = saleProceeds - totalSellFee;

    // Gains
    const grossGain = saleProceeds - purchaseCost;
    const netGain = netSaleProceeds - totalCostBasis;
    const totalFees = totalBuyFee + totalSellFee;

    // Percentages
    const grossGainPercent = (grossGain / purchaseCost) * 100;
    const netGainPercent = (netGain / totalCostBasis) * 100;
    const priceChange = ((sell - buy) / buy) * 100;

    // Per coin metrics
    const gainPerCoin = netGain / qty;
    const effectiveBuyPrice = totalCostBasis / qty;
    const effectiveSellPrice = netSaleProceeds / qty;

    setResult({
      purchaseCost,
      totalBuyFee,
      totalCostBasis,
      saleProceeds,
      totalSellFee,
      netSaleProceeds,
      grossGain,
      netGain,
      totalFees,
      grossGainPercent,
      netGainPercent,
      priceChange,
      gainPerCoin,
      effectiveBuyPrice,
      effectiveSellPrice,
      isProfitable: netGain > 0,
      isBreakEven: Math.abs(netGain) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setQuantity('');
    setBuyFee('');
    setSellFee('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'BTC: 0.1 @ $40K→$60K', buy: '40000', sell: '60000', qty: '0.1', buyFee: '0.1', sellFee: '0.1' },
    { label: 'ETH: 5 @ $1.5K→$2.5K', buy: '1500', sell: '2500', qty: '5', buyFee: '0.25', sellFee: '0.25' },
    { label: 'SOL: 50 @ $50→$150', buy: '50', sell: '150', qty: '50', buyFee: '0.5', sellFee: '0.5' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setQuantity(scenario.qty);
    setBuyFee(scenario.buyFee);
    setSellFee(scenario.sellFee);
  };

  return (
    <div className="space-y-8" data-testid="crypto-gain-calculator">
      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator Input Section */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <TrendingUp size={22} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Crypto Gain Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your realized cryptocurrency gains with fees</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 40000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Price per coin when purchased</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 60000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Price per coin when sold</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantity <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              step="0.00000001"
              min="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g., 0.5"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none text-lg"
              data-testid="quantity-input"
            />
            <p className="text-xs text-gray-500 mt-1">Number of coins traded</p>
          </div>
        </div>

        {/* Fee Section */}
        <div className="border-t border-gray-700 pt-4">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Trading Fees <span className="text-gray-500">(optional)</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={buyFee}
                  onChange={(e) => setBuyFee(e.target.value)}
                  placeholder="0.1"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 pr-8 py-2 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="buy-fee-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Buy/Entry fee</p>
            </div>
            <div>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={sellFee}
                  onChange={(e) => setSellFee(e.target.value)}
                  placeholder="0.1"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 pr-8 py-2 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="sell-fee-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Sell/Exit fee</p>
            </div>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
          data-testid="clear-btn"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-4">
          {/* Main Result - Net Gain */}
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-emerald-500/10 border-emerald-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? (
                  <TrendingUp className="text-emerald-400" size={28} />
                ) : result.isBreakEven ? (
                  <DollarSign className="text-yellow-400" size={28} />
                ) : (
                  <TrendingDown className="text-red-400" size={28} />
                )}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Net Gain' : result.isBreakEven ? 'Break Even' : 'Net Loss'}
                </span>
              </div>
              <button
                onClick={() => copyResult(result.netGain, 'gain')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied === 'gain' ? <CheckCircle2 size={18} className="text-emerald-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-emerald-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netGain >= 0 ? '+' : ''}{formatCurrency(result.netGain)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-emerald-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.netGainPercent)}
              </p>
              <p className="text-gray-400 text-sm mt-2">After all trading fees</p>
            </div>
          </div>

          {/* Summary Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Gross Gain</p>
              <p className={`text-xl font-semibold ${result.grossGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossGain >= 0 ? '+' : ''}{formatCurrency(result.grossGain)}
              </p>
              <p className="text-xs text-gray-500">{formatPercent(result.grossGainPercent)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Price Change</p>
              <p className={`text-xl font-semibold ${result.priceChange >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                {formatPercent(result.priceChange)}
              </p>
              <p className="text-xs text-gray-500">{formatCurrency(parseFloat(buyPrice))} → {formatCurrency(parseFloat(sellPrice))}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Total Fees Paid</p>
              <p className="text-xl font-semibold text-orange-400">-{formatCurrency(result.totalFees)}</p>
              <p className="text-xs text-gray-500">Buy + Sell fees</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Gain Per Coin</p>
              <p className={`text-xl font-semibold ${result.gainPerCoin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {result.gainPerCoin >= 0 ? '+' : ''}{formatCurrency(result.gainPerCoin)}
              </p>
              <p className="text-xs text-gray-500">Net profit per coin</p>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="p-5 bg-gray-800 rounded-xl">
            <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
              <Calculator size={16} className="text-emerald-400" />
              Transaction Breakdown
            </h4>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Buy Side */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-red-400 flex items-center gap-2">
                  <Minus size={14} /> Cost Basis (Buy Side)
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Purchase Cost:</span>
                    <span className="text-white">{formatCurrency(result.purchaseCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Buy Fee ({buyFee || 0}%):</span>
                    <span className="text-orange-400">+{formatCurrency(result.totalBuyFee)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-700 pt-2">
                    <span className="text-gray-300 font-medium">Total Cost Basis:</span>
                    <span className="text-white font-semibold">{formatCurrency(result.totalCostBasis)}</span>
                  </div>
                </div>
              </div>

              {/* Sell Side */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-green-400 flex items-center gap-2">
                  <Plus size={14} /> Proceeds (Sell Side)
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sale Proceeds:</span>
                    <span className="text-white">{formatCurrency(result.saleProceeds)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sell Fee ({sellFee || 0}%):</span>
                    <span className="text-orange-400">-{formatCurrency(result.totalSellFee)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-700 pt-2">
                    <span className="text-gray-300 font-medium">Net Proceeds:</span>
                    <span className="text-white font-semibold">{formatCurrency(result.netSaleProceeds)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Calculation */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Net Gain/Loss = {formatCurrency(result.netSaleProceeds)} - {formatCurrency(result.totalCostBasis)}</span>
                <span className={`text-xl font-bold ${result.netGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  = {result.netGain >= 0 ? '+' : ''}{formatCurrency(result.netGain)}
                </span>
              </div>
            </div>
          </div>

          {/* Effective Prices */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <Target size={16} className="text-blue-400" />
              Effective Prices (Including Fees)
            </h4>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-gray-400 text-sm">Effective Buy Price</p>
                <p className="text-xl font-semibold text-white">{formatCurrency(result.effectiveBuyPrice)}</p>
                <p className="text-xs text-gray-500">Cost basis per coin</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Effective Sell Price</p>
                <p className="text-xl font-semibold text-white">{formatCurrency(result.effectiveSellPrice)}</p>
                <p className="text-xs text-gray-500">Net proceeds per coin</p>
              </div>
            </div>
          </div>
          
          {/* Affiliate CTA Block */}
          <CryptoAffiliateBlock 
            profit={result.netGain} 
            cryptoName="crypto"
            toolName="Crypto Gain Calculator"
            toolSlug="crypto-gain-calculator"
          />
        </div>
      )}

      {/* Related Crypto Tools - Internal Links */}
      <div className="p-5 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-emerald-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/tools/crypto-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-green-400 transition-colors">Crypto Profit</p>
              <p className="text-sm text-gray-400">Quick profit calc</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400 transition-colors" />
          </Link>
          <Link
            to="/tools/bitcoin-profit-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-orange-400 transition-colors">Bitcoin Profit</p>
              <p className="text-sm text-gray-400">BTC calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-tax-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Crypto Tax</p>
              <p className="text-sm text-gray-400">Estimate taxes</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400 transition-colors" />
          </Link>
          <Link
            to="/tools/crypto-investment-calculator"
            className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
          >
            <div>
              <p className="font-medium text-white group-hover:text-blue-400 transition-colors">Investment</p>
              <p className="text-sm text-gray-400">Plan strategy</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
          </Link>
        </div>
      </div>

      {/* Tips Section */}
      <div className="p-5 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
        <h4 className="text-lg font-semibold text-white mb-3">💡 Understanding Crypto Gains</h4>
        <ul className="space-y-2 text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Gross vs Net:</strong> Gross gain ignores fees; net gain is what you actually keep after all costs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Fee Impact:</strong> Trading fees compound—0.1% on buy AND sell means ~0.2% off your gains</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Cost Basis:</strong> Your total purchase cost including fees—essential for accurate tax reporting</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>Taxable Events:</strong> Most jurisdictions tax realized gains when you sell—track every trade</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">•</span>
            <span><strong>FIFO vs LIFO:</strong> Different accounting methods affect your gains calculation for taxes</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CryptoGainCalculatorTool;
